import { useCallback, useEffect, useMemo, useState } from 'react';
import { HopperIcon } from '../ui/icons/HopperIcon';
import {
  cancelEventResponse,
  EventsApiError,
  fetchEvent,
  fetchEvents,
  fetchEventsSession,
  respondToEvent,
  type EventDetail,
  type EventFriendPreview,
  type EventResponseKind,
  type EventSummary,
  type EventsSessionResponse,
} from './eventsApi';
import { restoredCitySelection } from '../city/cityContext';
import './events.css';

type EventWhen = 'today' | 'week' | 'upcoming';
type LoadState = 'loading' | 'ready' | 'error';

type EventsScreenProps = {
  eventId?: string | null;
};

const AUTH_RETURN_TARGET_KEY = 'hopper.authReturnTarget';

const WHEN_TABS: Array<{ value: EventWhen; label: string }> = [
  { value: 'today', label: 'Сегодня' },
  { value: 'week', label: 'Ближайшие 7 дней' },
  { value: 'upcoming', label: 'Предстоящие' },
];

function safeTimezone(value: string | null | undefined): string {
  const timezone = value?.trim() || 'UTC';
  try {
    new Intl.DateTimeFormat('ru-RU', { timeZone: timezone }).format(new Date());
    return timezone;
  } catch {
    return 'UTC';
  }
}

function formatInstant(value: string | null, timezone: string | null | undefined, withDate = true): string {
  if (!value) return 'Время уточняется';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'Время уточняется';

  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: safeTimezone(timezone),
    day: withDate ? 'numeric' : undefined,
    month: withDate ? 'long' : undefined,
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatEventTime(event: Pick<EventSummary, 'starts_at' | 'ends_at' | 'venue'>): string {
  const timezone = event.venue.timezone;
  const start = formatInstant(event.starts_at, timezone);
  if (!event.ends_at) return start;

  const sameVenueDay = (() => {
    if (!event.starts_at || !event.ends_at) return false;
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: safeTimezone(timezone),
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    return formatter.format(new Date(event.starts_at)) === formatter.format(new Date(event.ends_at));
  })();

  return `${start} — ${formatInstant(event.ends_at, timezone, !sameVenueDay)}`;
}

function FriendsPreview({ people, count }: { people: EventFriendPreview[]; count: number }) {
  if (count <= 0 || people.length === 0) return null;

  return (
    <div className="events-friends" aria-label={`Друзей пойдут: ${count}`}>
      <div className="events-friends__avatars" aria-hidden="true">
        {people.slice(0, 5).map((person) => (
          person.avatar_url ? (
            <img key={person.id} src={person.avatar_url} alt="" />
          ) : (
            <span key={person.id}>{person.display_name?.trim().slice(0, 1).toUpperCase() || 'Х'}</span>
          )
        ))}
      </div>
      <span>{count === 1 ? '1 друг пойдёт' : `${count} друзей пойдут`}</span>
    </div>
  );
}

function EventCover({ event, large = false }: { event: EventSummary; large?: boolean }) {
  return event.cover_url ? (
    <img className={large ? 'events-cover events-cover--large' : 'events-cover'} src={event.cover_url} alt="" />
  ) : (
    <div className={large ? 'events-cover events-cover--large events-cover--empty' : 'events-cover events-cover--empty'}>
      <HopperIcon name="calendar" size={large ? 34 : 26} />
    </div>
  );
}

function EventCard({ event }: { event: EventSummary }) {
  return (
    <button
      className="events-card"
      type="button"
      onClick={() => window.location.assign(`/events/${encodeURIComponent(event.id)}`)}
    >
      <EventCover event={event} />
      <div className="events-card__body">
        <div className="events-card__place">
          <span>{event.venue.name || 'Заведение Хоппера'}</span>
          {event.city.name && <span>{event.city.name}</span>}
        </div>
        <h2>{event.title}</h2>
        <div className="events-card__time"><HopperIcon name="calendar" size={16} /><span>{formatEventTime(event)}</span></div>
        <div className="events-card__meta">
          <span>Идут: {event.going_count}</span>
          <span>Интересно: {event.interested_count}</span>
          {event.is_full && <strong>Мест нет</strong>}
        </div>
        <FriendsPreview people={event.friends_going_preview} count={event.friends_going_count} />
      </div>
    </button>
  );
}

function EventsList() {
  const [when, setWhen] = useState<EventWhen>('upcoming');
  const [events, setEvents] = useState<EventSummary[]>([]);
  const [state, setState] = useState<LoadState>('loading');
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cityReference, setCityReference] = useState<string | null>(() => restoredCitySelection()?.city ?? null);

  const load = useCallback(async (nextWhen: EventWhen, cursor: string | null = null) => {
    if (cursor) setLoadingMore(true);
    else setState('loading');

    try {
      const response = await fetchEvents(nextWhen, cursor, cityReference);
      setEvents((current) => cursor ? [...current, ...response.data] : response.data);
      setNextCursor(response.meta.next_cursor);
      setHasMore(response.meta.has_more);
      setState('ready');
    } catch {
      if (!cursor) setState('error');
    } finally {
      setLoadingMore(false);
    }
  }, [cityReference]);

  useEffect(() => {
    const onCityChanged = (event: Event) => {
      const city = (event as CustomEvent<{ city?: string }>).detail?.city ?? null;
      setCityReference(city);
    };
    window.addEventListener('hopper:city-changed', onCityChanged);
    return () => window.removeEventListener('hopper:city-changed', onCityChanged);
  }, []);

  useEffect(() => { void load(when); }, [load, when]);

  return (
    <main className="events-page" aria-label="События Хоппера">
      <div className="events-nav-row">
        <button className="events-back" type="button" onClick={() => window.location.assign('/pulse')}>
          <HopperIcon name="arrow-left" size={18} />
          <span>Пульс города</span>
        </button>
      </div>
      <header className="events-page__header">
        <div>
          <p>Городская афиша</p>
          <h1>События</h1>
          <span>Планы заведений и места, куда собираются ваши друзья.</span>
        </div>
      </header>

      <div className="events-tabs" role="tablist" aria-label="Период событий">
        {WHEN_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={when === tab.value}
            className={when === tab.value ? 'events-tab events-tab--active' : 'events-tab'}
            onClick={() => setWhen(tab.value)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {state === 'loading' && (
        <div className="events-grid" aria-label="Загружаем события">
          {Array.from({ length: 4 }, (_, index) => <div className="events-card events-card--skeleton" key={index} />)}
        </div>
      )}

      {state === 'error' && (
        <div className="events-state" role="alert">
          <strong>Не удалось загрузить события</strong>
          <span>Проверьте соединение и попробуйте ещё раз.</span>
          <button type="button" onClick={() => void load(when)}>Повторить</button>
        </div>
      )}

      {state === 'ready' && events.length === 0 && (
        <div className="events-state">
          <HopperIcon name="calendar" size={28} />
          <strong>На этот период событий пока нет</strong>
          <span>Загляните позже — новые события появляются после публикации заведениями.</span>
        </div>
      )}

      {state === 'ready' && events.length > 0 && (
        <>
          <div className="events-grid">{events.map((event) => <EventCard key={event.id} event={event} />)}</div>
          {hasMore && nextCursor && (
            <button className="events-load-more" type="button" disabled={loadingMore} onClick={() => void load(when, nextCursor)}>
              {loadingMore ? 'Загружаем…' : 'Показать ещё'}
            </button>
          )}
        </>
      )}
    </main>
  );
}

function EventDetailView({ eventId }: { eventId: string }) {
  const [event, setEvent] = useState<EventDetail | null>(null);
  const [session, setSession] = useState<EventsSessionResponse | null>(null);
  const [state, setState] = useState<LoadState>('loading');
  const [actionPending, setActionPending] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState('loading');
    try {
      const [detail, nextSession] = await Promise.all([fetchEvent(eventId), fetchEventsSession()]);
      setEvent(detail.event);
      setSession(nextSession);
      setState('ready');
    } catch {
      setState('error');
    }
  }, [eventId]);

  useEffect(() => { void load(); }, [load]);

  const startLogin = useCallback(() => {
    sessionStorage.setItem(AUTH_RETURN_TARGET_KEY, `/events/${eventId}`);
    window.location.assign('/api/v1/auth/yandex/redirect');
  }, [eventId]);

  const applyResponse = useCallback(async (kind: EventResponseKind | null) => {
    if (!session?.authenticated) {
      startLogin();
      return;
    }
    if (!event) return;

    setActionPending(true);
    setActionMessage(null);
    try {
      const status = kind === null
        ? await cancelEventResponse(event.id)
        : await respondToEvent(event.id, kind);
      setEvent((current) => current ? { ...current, ...status } : current);
    } catch (caught) {
      if (caught instanceof EventsApiError && caught.status === 422 && caught.code === 'event_full') {
        setActionMessage("Мест больше нет, но вы можете отметить «Интересно».");
      } else {
        setActionMessage('Не удалось обновить ответ. Попробуйте ещё раз.');
      }
    } finally {
      setActionPending(false);
    }
  }, [event, session?.authenticated, startLogin]);

  const venuePath = useMemo(() => event?.venue.id ? `/venue/${encodeURIComponent(event.venue.id)}` : null, [event?.venue.id]);

  if (state === 'loading') {
    return <main className="events-page"><div className="events-detail events-detail--loading" aria-label="Загружаем событие" /></main>;
  }

  if (state === 'error' || !event) {
    return (
      <main className="events-page">
        <div className="events-state" role="alert">
          <strong>Не удалось открыть событие</strong>
          <span>Возможно, оно больше недоступно или соединение прервалось.</span>
          <button type="button" onClick={() => void load()}>Повторить</button>
          <button type="button" className="events-secondary" onClick={() => window.location.assign('/events')}>К событиям</button>
        </div>
      </main>
    );
  }

  return (
    <main className="events-page" aria-label={event.title}>
      <div className="events-nav-row">
        <button className="events-back" type="button" onClick={() => window.location.assign('/events')}>
          <HopperIcon name="arrow-left" size={18} />
          <span>Все события</span>
        </button>
      </div>

      <article className="events-detail">
        <EventCover event={event} large />
        <div className="events-detail__body">
          <div className="events-detail__place">
            {venuePath ? (
              <button type="button" onClick={() => window.location.assign(venuePath)}>{event.venue.name || 'Заведение Хоппера'}</button>
            ) : (
              <span>{event.venue.name || 'Заведение Хоппера'}</span>
            )}
            {event.city.name && <span>{event.city.name}</span>}
          </div>
          <h1>{event.title}</h1>
          <div className="events-detail__time"><HopperIcon name="calendar" size={18} /><span>{formatEventTime(event)}</span></div>
          {event.description && <p className="events-detail__description">{event.description}</p>}

          <div className="events-detail__counts">
            <span><strong>{event.going_count}</strong> идут</span>
            <span><strong>{event.interested_count}</strong> интересно</span>
            {event.capacity !== null && <span><strong>{event.capacity}</strong> мест</span>}
            {event.is_full && <span className="events-full">Мест нет</span>}
          </div>

          <FriendsPreview people={event.friends_going_preview} count={event.friends_going_count} />

          {actionMessage && <div className="events-action-message" role="status">{actionMessage}</div>}

          <div className="events-response-actions" aria-label="Ваш ответ">
            <button
              type="button"
              className={event.my_response === 'going' ? 'events-response events-response--active' : 'events-response'}
              disabled={actionPending || (event.is_full && event.my_response !== 'going')}
              onClick={() => void applyResponse('going')}
            >
              Пойду
            </button>
            <button
              type="button"
              className={event.my_response === 'interested' ? 'events-response events-response--active' : 'events-response'}
              disabled={actionPending}
              onClick={() => void applyResponse('interested')}
            >
              Интересно
            </button>
            {event.my_response && (
              <button className="events-response events-response--cancel" type="button" disabled={actionPending} onClick={() => void applyResponse(null)}>
                Отменить ответ
              </button>
            )}
          </div>

          {!session?.authenticated && (
            <p className="events-login-hint">Чтобы отметить планы, войдите в Хоппер через существующий аккаунт Яндекса.</p>
          )}
        </div>
      </article>
    </main>
  );
}

export function EventsScreen({ eventId = null }: EventsScreenProps) {
  return eventId ? <EventDetailView eventId={eventId} /> : <EventsList />;
}
